import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

/**
 * Skickar page_view till GA4 vid varje ruttbyte.
 *
 * Snippeten i index.html är konfigurerad med send_page_view: false — hela
 * mätningen går genom den här kroken. Anropas från Routing(), som är förälder
 * till sidkomponenterna: React kör barnens effekter före förälderns, så
 * usePageTitle hinner sätta document.title innan träffen skickas. Ligger den
 * i stället på sidnivå rapporteras föregående sidas titel.
 *
 * search tas med i page_location men inte hash — /tabell?season=2025 är olika
 * vyer, ankarlänkar är det inte.
 *
 * Träffen skickas oavsett samtycke. Det är Consent Mode som avgör vad som
 * faktiskt får lagras: utan samtycke går den iväg cookielös och utan
 * identifierare.
 */
export function usePageview() {
  const { pathname, search } = useLocation()

  useEffect(() => {
    window.gtag?.('event', 'page_view', {
      page_path: pathname + search,
      page_location: window.location.origin + pathname + search,
      page_title: document.title,
    })
  }, [pathname, search])
}

/**
 * Nyckeln läses även av den inline-satta snippeten i index.html. Ändras den
 * här måste den ändras där.
 */
const CONSENT_KEY = 'mt.samtycke'

export type Consent = 'granted' | 'denied'

/** null = obesvarat, dvs. bannern ska visas. */
export function readConsent(): Consent | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    return stored === 'granted' || stored === 'denied' ? stored : null
  } catch {
    // Privat läge utan localStorage: bannern visas varje besök. Att i stället
    // dölja den hade varit att anta ett samtycke som aldrig getts.
    return null
  }
}

export function writeConsent(value: Consent) {
  try {
    localStorage.setItem(CONSENT_KEY, value)
  } catch {
    // Går det inte att spara gäller valet ändå för den här sidladdningen.
  }
  window.gtag?.('consent', 'update', { analytics_storage: value })
}
